import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * The six screens that are not Today or Tasks.
 *
 * A menu rather than a scrolling tab strip: the six are unrelated destinations, and a strip that
 * scrolls hides its contents behind a gesture nobody performs on a control they cannot see is
 * scrollable. The button says how many are behind it only by staying the same width — the count
 * is fixed, so a number would be noise.
 *
 * Dismissal follows the notification popover exactly: pointerdown elsewhere, or Escape.
 */
export function MoreMenu<T extends string>({
  screen,
  onPick,
  items,
  extra,
}: {
  screen: T;
  onPick: (next: T) => void;
  items: { id: T; label: string }[];
  /**
   * Something that is not a screen, at the end of the menu.
   *
   * The `?` lives here on a phone. It cannot live in the header at that width — four icons beside
   * two tabs and this trigger wrap the nav onto another line, which puts the first task 285px down
   * an 812px screen and past the top third, a finding of its own. And it cannot stay hidden: the
   * screen-scoped help was switched off exactly where the interface explains itself least.
   *
   * A menu the phone user already opens to navigate is the one place with room for it.
   */
  extra?: { label: string; onPick: () => void };
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const here = items.find((i) => i.id === screen);

  return (
    <div className="more-menu" ref={wrapper}>
      {/*
        When you are on one of the six, the button names it. Otherwise the active tab would be
        invisible — you would be on Habits with Today and Tasks both looking unselected and
        nothing saying where you are.
      */}
      <button
        type="button"
        className={here ? 'tab active' : 'tab'}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-current={here ? 'page' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {here ? here.label : 'More'}
        <Icon name="caret" />
      </button>

      {open && (
        <div className="more-panel card" role="menu" aria-label="More screens">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className={item.id === screen ? 'more-item is-here' : 'more-item'}
              onClick={() => {
                onPick(item.id);
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
          {extra && (
            <button
              type="button"
              role="menuitem"
              className="more-item more-extra"
              onClick={() => {
                extra.onPick();
                setOpen(false);
              }}
            >
              {extra.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
